import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {map} from 'rxjs/operators';

import {
  IUserGuide,
  IUserGuideActiveStep,
  IUserGuideStep,
} from './user-guide.model';
import {UserGuideService} from './user-guide.service';

@Injectable({
  providedIn: 'root',
})
export class UserGuideStore {
  private _userGuide$: BehaviorSubject<IUserGuide[]> = new BehaviorSubject([]);
  private _activeStep$: Subject<IUserGuideActiveStep> = new Subject();

  constructor(
    private httpClient: HttpClient,
    private userGuideService: UserGuideService
  ) {
    this.httpClient
      .get<IUserGuide[]>('/index.php/site/signup-wizard-config')
      .pipe(
        map(this.setStepElementsForTestServer),
        map((guide: IUserGuide[]) => {
          return guide.map((section: IUserGuide, index: number) => ({
            ...section,
            steps: section.steps.map((step, index: number) => ({
              ...step,
              index,
            })),
            index,
            isActive: index === 0 ? true : false,
            isComplete: false,
          }));
        })
      )
      .subscribe((guide: IUserGuide[]) => this._userGuide$.next(guide));
  }

  get userGuide$(): Observable<IUserGuide[]> {
    return this._userGuide$.asObservable();
  }

  get activeStep$(): Observable<IUserGuideActiveStep> {
    return this._activeStep$.asObservable();
  }

  toggleActiveSection(index: number): void {
    const updatedGuide = this._userGuide$.value.reduce(
      (acc: IUserGuide[], curr: IUserGuide) => {
        curr.isActive = index === curr.index ? true : false;
        return [...acc, curr];
      },
      []
    );

    this._userGuide$.next(updatedGuide);
  }

  setActiveStep(parentIndex: number, stepIndex: number): void {
    this.userGuideService.isDrawerOpen$.next(false);

    const parent: IUserGuide = this._userGuide$.value[parentIndex];
    const step: IUserGuideStep = parent.steps[stepIndex];
    const hasNext: boolean = !!parent.steps[stepIndex + 1];
    const parentNumberOfSteps: number = parent.steps.length;

    this._activeStep$.next({
      step,
      hasNext,
      parentIndex,
      parentNumberOfSteps,
    });
  }

  clearActiveStep(isComplete?: boolean, parentIndex?: number): void {
    if (isComplete) {
      const updatedGuide = this._userGuide$.value.reduce(
        (acc: IUserGuide[], curr: IUserGuide) => {
          curr.isActive = false;
          curr.isComplete = parentIndex === curr.index ? true : curr.isComplete;
          return [...acc, curr];
        },
        []
      );

      const nextSection: IUserGuide = this._userGuide$.value[parentIndex + 1];
      if (nextSection) {
        nextSection.isActive = true;
      }

      this._userGuide$.next(updatedGuide);
    }

    this.userGuideService.isDrawerOpen$.next(true);
    this._activeStep$.next(null);
  }

  private setStepElementsForTestServer(guide: IUserGuide[]): IUserGuide[] {
    if (!location.hostname.includes('test')) {
      return guide;
    }

    return guide.reduce(
      (acc: IUserGuide[], curr: IUserGuide, index: number) => {
        if (index === 1) {
          curr.steps[0].element.dataAttr = 'drawing-tools-cat-1';
          curr.steps[1].element.dataAttr = 'drawing-tools-l2-21';
          curr.steps[2].element.dataAttr = 'drawing-tools-item-4';
        }

        return [...acc, curr];
      },
      []
    );
  }
}
